import { useMemo } from "react";
import type { ProgrammeGroupSummary } from "../../lib/api/overview";

export interface UnitOption {
  id: string;
  name: string;
  code: string;
  groupName: string;
}

export function flattenProgrammeUnits(
  groups: ProgrammeGroupSummary[] | undefined,
): UnitOption[] {
  if (!groups) {
    return [];
  }
  return groups.flatMap((group) =>
    group.units.map((unit) => ({
      id: unit.id,
      name: unit.name,
      code: unit.code,
      groupName: group.name,
    })),
  );
}

export interface AllocationLineForm {
  unitId: string;
  fundId: string;
  categoryId: string;
  amount: string;
  costType?: string;
}

export function sumAllocationAmounts(lines: AllocationLineForm[]): number {
  return lines.reduce((sum, line) => sum + (Number.parseFloat(line.amount) || 0), 0);
}

export function buildPaymentLinesFromAmount(
  expenseLines: { id: string; amount: string }[],
  existingPayments: { lines: { expenseLineId: string; amount: string }[] }[],
  paymentAmount: string,
): { expenseLineId: string; amount: string }[] {
  let remaining = Number.parseFloat(paymentAmount);
  if (!Number.isFinite(remaining) || remaining <= 0) {
    return [];
  }

  const paidByLine = new Map<string, number>();
  for (const payment of existingPayments) {
    for (const line of payment.lines) {
      paidByLine.set(
        line.expenseLineId,
        (paidByLine.get(line.expenseLineId) ?? 0) + Number.parseFloat(line.amount),
      );
    }
  }

  const result: { expenseLineId: string; amount: string }[] = [];
  for (const line of expenseLines) {
    if (remaining <= 0) {
      break;
    }
    const approved = Number.parseFloat(line.amount);
    const paid = paidByLine.get(line.id) ?? 0;
    const unpaid = Math.max(0, approved - paid);
    if (unpaid <= 0) {
      continue;
    }
    const pay = Math.min(remaining, unpaid);
    result.push({
      expenseLineId: line.id,
      amount: pay.toFixed(2),
    });
    remaining -= pay;
  }

  return result;
}

export function useReferenceMaps(
  units: UnitOption[],
  funds: { id: string; name: string }[] | undefined,
  categories: { id: string; name: string }[] | undefined,
  banks: { id: string; name: string }[] | undefined,
) {
  return useMemo(
    () => ({
      unitName: (id: string) => units.find((u) => u.id === id)?.name ?? id,
      fundName: (id: string) => funds?.find((f) => f.id === id)?.name ?? id,
      categoryName: (id: string) => categories?.find((c) => c.id === id)?.name ?? id,
      bankName: (id: string) => banks?.find((b) => b.id === id)?.name ?? id,
    }),
    [units, funds, categories, banks],
  );
}
